/**
 * Direct Klaviyo newsletter subscription (client-side).
 *
 * Intercepts every <form data-klaviyo-newsletter> submit and posts the email
 * straight to Klaviyo's public client subscription endpoint instead of routing
 * it through Shopify's /contact customer form.
 *
 * The target list uses double opt-in in Klaviyo, so a successful call (202)
 * means Klaviyo has SENT the confirmation email - the visitor is not yet
 * subscribed until they click the link. The success message reflects that.
 *
 * Config comes from data-attributes on each form (company id + list id are
 * rendered from theme settings in snippets/newsletter-form.liquid).
 */
(function () {
  'use strict';

  // Guard: the loader <script> lives in a snippet that can appear multiple
  // times per page (footer + popup + section). Only wire up the listener once.
  if (window.__dnKlaviyoNewsletterInit) return;
  window.__dnKlaviyoNewsletterInit = true;

  var ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
  var REVISION = '2024-10-15';

  function clearNode(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
  }

  function buildBanner(message, status, alignment) {
    var banner = document.createElement('div');
    banner.className = 'banner banner--' + status + ' text-' + (alignment || 'center');
    var content = document.createElement('div');
    content.className = 'banner__content';
    content.textContent = message;
    banner.appendChild(content);
    return banner;
  }

  function showFeedback(form, message, status) {
    var slot = form.querySelector('[data-klaviyo-feedback]');
    if (!slot) return;
    clearNode(slot);
    slot.appendChild(buildBanner(message, status, form.getAttribute('data-banner-alignment')));
    slot.hidden = false;
  }

  function setLoading(form, isLoading) {
    var button = form.querySelector('[type="submit"]');
    if (!button) return;
    if (isLoading) {
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
    } else {
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  }

  function handleSuccess(form) {
    // Mirror the original server-side behaviour: replace the fields with the
    // confirmation notice. Hide every child except the feedback slot.
    Array.prototype.forEach.call(form.children, function (child) {
      if (!child.hasAttribute('data-klaviyo-feedback')) child.hidden = true;
    });
    showFeedback(form, form.getAttribute('data-confirm-message'), 'success');

    // Popup: set the same frequency-cap flag the old inline script used, so the
    // popup no longer auto-opens for this visitor.
    if (form.hasAttribute('data-klaviyo-popup')) {
      try { localStorage.setItem('theme:popup-filled', 'true'); } catch (e) {}
    }
  }

  function subscribe(form) {
    var emailInput = form.querySelector('input[type="email"]');
    var companyId = form.getAttribute('data-company-id');
    var listId = form.getAttribute('data-list-id');
    var email = emailInput ? emailInput.value.trim() : '';

    if (!email || !companyId || !listId) {
      showFeedback(form, form.getAttribute('data-error-message'), 'error');
      return;
    }

    setLoading(form, true);

    var payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: form.getAttribute('data-source') || 'Newsletter',
          profile: {
            data: {
              type: 'profile',
              attributes: { email: email }
            }
          }
        },
        relationships: {
          list: { data: { type: 'list', id: listId } }
        }
      }
    };

    fetch(ENDPOINT + '?company_id=' + encodeURIComponent(companyId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: REVISION },
      body: JSON.stringify(payload)
    })
      .then(function (response) {
        setLoading(form, false);
        if (response.ok) {
          handleSuccess(form);
          return;
        }
        return response.text().then(function (body) {
          console.error('[klaviyo-newsletter] Unexpected status ' + response.status + ': ' + body);
          showFeedback(form, form.getAttribute('data-error-message'), 'error');
        });
      })
      .catch(function (error) {
        setLoading(form, false);
        console.error('[klaviyo-newsletter] Network error', error);
        showFeedback(form, form.getAttribute('data-error-message'), 'error');
      });
  }

  // Delegated listener so late-injected forms (popup) are covered too.
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || typeof form.matches !== 'function') return;
    if (!form.matches('form[data-klaviyo-newsletter]')) return;
    event.preventDefault();
    subscribe(form);
  });
})();
